import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Table, Card, Tag, Typography, Select } from 'antd'
import { getTransactions } from '../../services/balance'
import { formatTimestamp } from '../../utils/format'
import { billingReasonText } from '../subscriptions/subscriptionLabels'
import type { BalanceTransaction, Pagination } from '../../types'

const { Title } = Typography

const typeLabels: Record<string, string> = {
  PAYMENT: '收款入賬',
  SETTLEMENT: '結算',
  PAYOUT: '結款',
  FREEZE: '凍結',
  UNFREEZE: '解凍',
  CHARGEBACK: '拒付',
  CHARGEBACK_FEE: '拒付手續費',
  CHARGEBACK_RECOVERY: '拒付回款',
  ROLLING_RESERVE_RECOVERY: '循環保證金恢復',
  REFUND: '退款',
  FEE: '費用',
  ROLLING_RESERVE: '循環保證金扣留',
  ROLLING_RESERVE_RELEASE: '循環保證金釋放',
  ROLLING_RESERVE_OFFSET: '循環保證金沒收',
  ADMIN_RECHARGE: '管理員充值', BALANCE_CORRECTION: '帳務沖正'
}

const typeColors: Record<string, string> = {
  PAYMENT: 'green',
  SETTLEMENT: 'blue',
  PAYOUT: 'orange',
  FREEZE: 'red',
  UNFREEZE: 'cyan',
  CHARGEBACK: 'red',
  CHARGEBACK_FEE: 'volcano',
  CHARGEBACK_RECOVERY: 'green',
  ROLLING_RESERVE_RECOVERY: 'green',
  REFUND: 'orange',
  FEE: 'default',
  ROLLING_RESERVE: 'purple',
  ROLLING_RESERVE_RELEASE: 'geekblue',
  ROLLING_RESERVE_OFFSET: 'magenta',
  ADMIN_RECHARGE: 'green', BALANCE_CORRECTION: 'volcano'
}

export default function Transactions() {
  const [transactions, setTransactions] = useState<BalanceTransaction[]>([])
  const [pagination, setPagination] = useState<Pagination>({ page: 1, size: 20, total: 0, totalPages: 0 })
  const [loading, setLoading] = useState(true)
  const [type, setType] = useState<string | undefined>()
  const [pageSize, setPageSize] = useState(20)

  const fetchTransactions = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    getTransactions({ page, size, type })
      .then((res) => {
        setTransactions(res.data.items)
        setPagination(res.data.pagination)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchTransactions()
  }, [type])

  const formatAmount = (amount: string | number, currency: string) => {
    const num = Number(amount)
    const value = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    return num >= 0 ? `+${value} ${currency}` : `-${value} ${currency}`
  }

  const columns = [
    {
      title: '類型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={typeColors[type]}>{typeLabels[type] || type}</Tag>
      )
    },
    {
      title: '金額',
      key: 'amount',
      render: (_: unknown, record: BalanceTransaction) => (
        <span style={{ color: Number(record.amount) >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {formatAmount(record.amount, record.currency)}
        </span>
      )
    },
    { title: '描述', dataIndex: 'description', key: 'description' },
    {
      title: '關聯單據',
      key: 'source',
      render: (_: unknown, record: BalanceTransaction) => (
        <div>
          {record.relatedOrderId ? (
            <Link to={`/dashboard/orders/${record.relatedOrderId}`}>{record.relatedOrderId}</Link>
          ) : (record.sourceId || '-')}
          {record.relatedOrderId && record.subscription && (
            <div style={{ marginTop: 4 }}>
              <Tag color="blue">訂閱賬單 #{record.subscription.cycleNo}</Tag>
              <Typography.Text type="secondary">
                {billingReasonText[record.subscription.billingReason] || record.subscription.billingReason}
              </Typography.Text>
            </div>
          )}
          {record.relatedOrderId && record.sourceId && record.sourceId !== record.relatedOrderId && (
            <div><Typography.Text type="secondary">來源：{record.sourceId}</Typography.Text></div>
          )}
        </div>
      )
    },
    {
      title: '時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (ts: number) => formatTimestamp(ts)
    },
  ]

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>交易明細</Title>
      <Card>
        <Select
          placeholder="交易類型"
          allowClear
          style={{ width: 150, marginBottom: 16 }}
          value={type}
          onChange={setType}
          options={Object.entries(typeLabels).map(([value, label]) => ({ value, label }))}
        />
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.size,
            total: pagination.total,
            onChange: (page, size) => {
              if (size !== pageSize) {
                setPageSize(size)
                fetchTransactions(1, size)
              } else {
                fetchTransactions(page, size)
              }
            },
          }}
        />
      </Card>
    </div>
  )
}
